import { useState } from 'react';
import type { DriveSearchResponse, ExistingDocument, RolloutAction, RolloutDraft } from '../types';
import { Button, Card, Notice, Section, SummaryRow } from '../components/ui/primitives';
import { Modal } from '../components/ui/Modal';

/**
 * Screen 3 — Document Destination.
 *
 * Shows exactly where the rollout will land before anything is written. When a
 * document already exists it is never modified silently: the user must pick
 * "Add New Tab" or "Create New Document".
 */

function formatModified(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function DocumentCard({ document: doc }: { document: ExistingDocument }) {
  return (
    <div className="rounded-input border border-line bg-canvas px-4 py-3.5">
      <dl className="space-y-1.5">
        <SummaryRow label="Document Name">{doc.name}</SummaryRow>
        <SummaryRow label="Folder">{doc.folderPath || doc.folderName}</SummaryRow>
        <SummaryRow label="Last Modified">{formatModified(doc.modifiedTime)}</SummaryRow>
        <SummaryRow label="Google Docs link">
          <a
            href={doc.webViewLink}
            target="_blank"
            rel="noreferrer noopener"
            className="text-accent underline-offset-2 hover:underline"
          >
            Open in a new tab
          </a>
        </SummaryRow>
      </dl>
    </div>
  );
}

export function DestinationScreen({
  draft,
  search,
  tabTitlePreview,
  submitting,
  onBack,
  onSubmit,
}: {
  draft: RolloutDraft;
  search: DriveSearchResponse;
  /** e.g. "20/08/2026 - META" — what the new tab or section will be called. */
  tabTitlePreview: string;
  submitting: boolean;
  onBack: () => void;
  onSubmit: (action: RolloutAction, documentId?: string) => void;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  const existing = search.existing;
  const businessTypeLabel = draft.businessType === 'AGENCY' ? 'Via Agency' : 'Direct Brand';

  return (
    <div className="space-y-5">
      <Card className="p-5 sm:p-7">
        <div className="space-y-8">
          <Section title="Document Destination">
            <dl className="space-y-2.5">
              <SummaryRow label="Business Type">{businessTypeLabel}</SummaryRow>
              {draft.businessType === 'AGENCY' ? (
                <SummaryRow label="Agency">{draft.record.agencyName}</SummaryRow>
              ) : null}
              <SummaryRow label="Brand">{draft.record.brandName}</SummaryRow>
              <SummaryRow label="Channel">{draft.integration.channel}</SummaryRow>
              <SummaryRow label="Destination Folder">
                {search.destinationFolderPath}
                {search.folderWillBeCreated ? (
                  <span className="ml-2 rounded-full bg-accent-soft px-2 py-px text-[10px] font-medium text-accent">
                    will be created
                  </span>
                ) : null}
              </SummaryRow>
              <SummaryRow label="Document Name">
                {existing ? existing.name : search.proposedDocumentName}
              </SummaryRow>
              <SummaryRow label="Tab Name">{tabTitlePreview}</SummaryRow>
            </dl>
          </Section>

          {existing ? (
            <Section title="Existing Document Found">
              <p className="text-micro text-ink-muted">
                A rollout document already exists for this{' '}
                {draft.businessType === 'AGENCY' ? 'agency and brand' : 'brand'}. Nothing has been
                changed — choose what should happen next.
              </p>

              <DocumentCard document={existing} />

              {search.duplicates.length ? (
                <Notice tone="warning" title={`${search.duplicates.length} other possible match${search.duplicates.length === 1 ? '' : 'es'}`}>
                  <p className="mb-1">
                    More than one document matched. The most recently modified one is shown above.
                  </p>
                  <ul className="list-disc space-y-0.5 pl-4">
                    {search.duplicates.map((duplicate) => (
                      <li key={duplicate.documentId}>
                        <a
                          href={duplicate.webViewLink}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-accent underline-offset-2 hover:underline"
                        >
                          {duplicate.name}
                        </a>{' '}
                        &mdash; {duplicate.folderPath || duplicate.folderName}
                      </li>
                    ))}
                  </ul>
                </Notice>
              ) : null}

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button onClick={() => setModalOpen(true)}>Add New Tab</Button>
                <Button variant="secondary" onClick={() => onSubmit('CREATE_DOCUMENT')}>
                  Create New Document
                </Button>
              </div>
            </Section>
          ) : (
            <Section title="New Document">
              <p className="text-micro text-ink-muted">
                No existing rollout document was found, so a new one will be created.
              </p>
              <Button onClick={() => onSubmit('CREATE_DOCUMENT')} loading={submitting}>
                Create Document
              </Button>
            </Section>
          )}
        </div>
      </Card>

      <div className="flex justify-start">
        <Button variant="secondary" onClick={onBack}>
          Back
        </Button>
      </div>

      {/*
        Adding to an existing document is the one action that touches someone
        else's work, so it is confirmed explicitly and the dialog is not
        dismissible by a stray backdrop click.
      */}
      <Modal
        open={modalOpen && Boolean(existing)}
        onClose={() => setModalOpen(false)}
        dismissible
        width="lg"
        title="Add a new tab to this document?"
        description="The existing content is left untouched. A new tab is added at the end."
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={submitting}
              onClick={() => existing && onSubmit('ADD_TAB', existing.documentId)}
            >
              Add New Tab
            </Button>
          </>
        }
      >
        {existing ? (
          <div className="space-y-4">
            <DocumentCard document={existing} />
            <dl className="space-y-1.5">
              <SummaryRow label="New tab name">{tabTitlePreview}</SummaryRow>
            </dl>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
